import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function AIHospitalityRevolutionPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'AIホスピタリティ革命：5つの変革' }
  ];

  const article = {
    category: '業界トレンド・テクノロジー統合',
    date: '2025年8月14日',
    title: 'AIホスピタリティ革命：5つの変革',
    subtitle: '人工知能が切り開く執事サービスの未来',
    heroImage: '/images/generated/jba-fs-046-news_ai-hospitality-revolution.jpg',
    lead: 'Hospitality Netの専門家Kate Fairweather氏が、ロンドンSavoy Hotelのヘッドバトラーとの交流を通じて得た洞察をもとに、AI技術がホスピタリティ業界に与える5つの革命的変革を分析しました。人間の執事サービスとAI技術の相補的関係から、AIデトックス・トレンドまで、業界の未来を包括的に展望した注目の論考です。',
    sections: [
      {
        heading: '拡張された感情的知性',
        image: '/images/generated/jba-fs-047-news_ai-hospitality-revolution-v3.jpg', aspect: 'aspect-[3/4]',
        alt: 'ゲストの感情や暗黙のニーズをデータで捉え、人間のスタッフの直感を補強するAIのイメージ',
        paragraphs: [
          'AI技術は数千のゲストインタラクションを分析し、感情のリズム、ストレス指標、暗黙のニーズのパターンを理解することで、人間スタッフにデータ駆動の洞察を提供します。これにより、執事はゲストの深いニーズをより正確に予測できるようになります。',
          'Hiltonの「Connie」のようなAIコンシェルジュは、単なる情報提供を超えて、すべてのインタラクションから学習し、ゲストが本当に求めているものを理解します。将来的には、音声パターンからストレスレベルを検出し、微表情から好みを識別する「ターボチャージされた人間の直感」が実現されるでしょう。'
        ]
      },
      {
        heading: 'レスポンシブ環境の実現',
        image: '/images/generated/jba-fs-048-news_ai-hospitality-revolution-v4.jpg', aspect: 'aspect-[4/3]',
        alt: 'ゲストの概日リズムや睡眠パターンに合わせて照明・温度を調整する客室のイメージ',
        paragraphs: [
          'AI洞察とスマートデバイスの融合により、標準化された静的なホスピタリティスペースが動的でレスポンシブな環境に変わります。客室が個人の概日リズムに合わせて照明を調整し、睡眠パターンに合わせて温度を調整し、カスタマイズされた背景ノイズを提供する未来が実現されます。',
          '時差ぼけを認識すると照明を調整しリラクゼーション技術を提案し、お祝いを認識すると雰囲気を高めるなど、空間が文脈を真に理解し、望ましい結果を最適に作り出すように環境を調整します。'
        ]
      },
      {
        heading: 'スマートサステナビリティの飛躍',
        image: '/images/generated/jba-fs-049-news_ai-hospitality-revolution-v3.jpg', aspect: 'aspect-video',
        alt: '占有データを監視して部屋ごとにエネルギーを最適化するAI管理システムのイメージ',
        paragraphs: [
          'Accor Hotelsのように、物件全体にAI搭載エネルギー管理システムを統合し、リアルタイム占有データを監視して部屋ごとに照明、暖房、冷房を最適化する取り組みが始まっています。AIツールはレストランの需要を予測し、食品廃棄物を大幅に削減します。',
          '将来的には、行動、気象パターン、地域イベント、履歴データを分析する予測システムによる部屋ごと・時間ごとのエネルギー消費最適化が実現し、リアルタイムリソース最適化により無駄を最小限に抑えながらゲストの快適性を維持できるようになります。'
        ]
      },
      {
        heading: '近未来のノベルティ体験',
        image: '/images/generated/jba-fs-050-news_ai-hospitality-revolution-v3.jpg', aspect: 'aspect-[3/4]',
        alt: 'AIが強化した音と光による没入型空間を体験するゲストのイメージ',
        paragraphs: [
          'Radisson Hotel GroupのAIを活用した没入型「infinity room」のように、革新的なホテルは創造的なAIアプリケーションを実験しています。AIが強化した音と光を使用して、イベントプランナーがスペースをリアルタイムで視覚化できる技術が開発されています。',
          '近い将来、AIコンシェルジュによるレストラン予約や旅行プランのカスタマイズ、地元の旬の食材を使ってゲストの正確な好みに合わせた食事を調整するAIシェフ、ゲストの美容やウェルネス目標を満たすAIスパトリートメントなどが実現されるでしょう。'
        ]
      },
      {
        heading: 'AIデトックス・トレンドの台頭',
        image: '/images/generated/jba-fs-051-news_ai-hospitality-revolution-v3.jpg', aspect: 'aspect-[3/4]',
        alt: 'テクノロジーから離れ、人間的で職人的なもてなしを求めるスローホスピタリティのイメージ',
        paragraphs: [
          'すべてがスマート、予測的、適応的であるAIの未来では、慢性的に非AI体験への欲求が生まれる可能性があります。デジタル中毒が増加したときに「ダム」フォンへの関心が戻ったように、AIデトックスリトリートやダムホテルへの需要が高まるでしょう。',
          '「非拡張」スタッフを売りにする目的地や、AIが急増するにつれて高級、職人的、不完全、スローホスピタリティの復活も見られる可能性があり、人間の執事サービスの価値が再評価されることが予想されます。'
        ]
      }
    ],
    comment: 'Kate Fairweather氏による5つの変革分析は、AI技術と人間の執事サービスの相補的関係を見事に示した洞察に富む論考として、弊協会としても深く共感いたします。特に「拡張された感情的知性」の概念は、私どもが重視する「察する心」の技術的進化として大変興味深いものです。AIデトックス・トレンドの指摘は、テクノロジーが進歩するほど人間の温かみのあるサービスの価値が高まることを示しており、執事という職業の本質的価値を再確認させるものです。日本の「おもてなし」文化とAI技術の融合は、世界に誇れる新しいホスピタリティモデルを創造する可能性を秘めており、私どもとしても、伝統的な人間力とテクノロジーを調和させた次世代の執事サービスの確立に向けて、積極的に取り組んでまいります。',
    source: { name: 'Hospitality Net（Kate Fairweather著）', url: 'https://www.hospitalitynet.org/opinion/4128541.html', access: '2025年10月' },
    related: [
      { to: '/news/apple-robot-butler', image: '/images/generated/jba-fs-052-news_ai-hospitality-revolution-v3.jpg', aspect: 'aspect-[3/4]', alt: 'Apple ロボットバトラー開発プロジェクトの関連記事', title: 'Apple ロボットバトラー開発プロジェクト', desc: 'AI技術が切り開く執事サービスの未来' },
      { to: '/news/raffles-sentosa-butler-package', image: '/images/generated/jba-fs-053-news_ai-hospitality-revolution-v3.jpg', aspect: 'aspect-[3/4]', alt: 'Raffles Sentosa Singapore「The Butler Did It」パッケージの関連記事', title: 'Raffles Sentosa Singapore「The Butler Did It」パッケージ', desc: '超高額パッケージにおける執事サービスの価値' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: '出典元', value: 'Hospitality Net（Kate Fairweather著）', link: false },
    { label: '元記事URL', value: 'https://www.hospitalitynet.org/opinion/4128541.html', link: true },
    { label: '市場規模データ', value: 'https://www.kingsresearch.com/ai-in-hospitality-market-671', link: true },
    { label: '導入率統計', value: 'https://www.escoffier.edu/blog/world-food-drink/hospitality-industry-trends-and-statistics/', link: true },
    { label: 'Connie（IBM）', value: 'https://uk.newsroom.ibm.com/2016-Mar-09-Hilton-and-IBM-Pilot-Connie-The-Worlds-First-Watson-Enabled-Hotel-Concierge', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];
  const toc = [
    { id: 's-emotional', label: '拡張された感情的知性' },
    { id: 's-responsive', label: 'レスポンシブ環境の実現' },
    { id: 's-sustainability', label: 'スマートサステナビリティの飛躍' },
    { id: 's-market', label: 'AIホスピタリティ市場の実データ' },
    { id: 's-novelty', label: '近未来のノベルティ体験' },
    { id: 's-detox', label: 'AIデトックス・トレンドの台頭' },
    { id: 's-connie', label: '実例：ロボットコンシェルジュ「Connie」' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Technology" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                'Hospitality Netの専門家Kate Fairweather氏が、AIがホスピタリティ業界にもたらす5つの変革を分析した',
                '感情的知性の拡張・レスポンシブ環境・スマートサステナビリティなど、AIと人間の相補的関係が論の軸に置かれた',
                'AIが普及するほど「非拡張」の人間的サービスへの揺り戻し（AIデトックス）が起こり、執事サービスの価値が再評価される'
              ]}
            />

            <SectionHeading id="s-emotional" en="Emotional Intelligence">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="テクノロジーが進歩するほど、人間の執事が提供する温かみのあるサービスの価値は高まっていく。" cite="Kate Fairweather / Hospitality Net" />
            </div>

            <SectionHeading id="s-responsive" en="Responsive Space">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-sustainability" en="Sustainability">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-market" en="Market Data">AIホスピタリティ市場の実データ</SectionHeading>
            <div className="space-y-6">
              <p className={P}>調査会社Kings Researchによると、ホスピタリティ分野のAI市場は2023年時点で約<Stat>163億ドル</Stat>規模に達し、2024年から2031年にかけて年平均成長率20.36%で拡大、2031年には約<Stat>703億ドル</Stat>に達すると予測されています。人間のもてなしを支える技術基盤として、AIへの投資が急速に進んでいることを示す数字です。</p>
              <p className={P}>導入も現実のものとなっており、料理学校Escoffierがまとめた業界統計では、ホテルの顧客サービスでAIを活用する割合は<Stat>89%</Stat>、ハウスキーピングのスケジューリングでは<Stat>64%</Stat>に達しています。AIはすでに実務の効率化に組み込まれ、人間のスタッフをより創造的で対人的な役割へと解放しつつあります。</p>
            </div>
            <SourceShot
              src="/images/news-sources/aihosp-wikipedia.jpg"
              alt="ホスピタリティ産業の概況を解説するWikipedia英語版のページのスクリーンショット"
              media="Wikipedia（Hospitality industry）"
              url="https://en.wikipedia.org/wiki/Hospitality_industry"
            />

            <SectionHeading id="s-novelty" en="Novelty">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-detox" en="AI Detox">{S[4].heading}</SectionHeading>
            <div className="space-y-6">{S[4].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-connie" en="Case Study">実例：Hiltonのロボットコンシェルジュ「Connie」</SectionHeading>
            <div className="space-y-6">
              <p className={P}>本記事が触れるHiltonの「Connie」は、2016年3月に米バージニア州の Hilton McLean で試験導入された、IBM Watson を活用した世界初のロボットコンシェルジュです。ホテルのアメニティ、周辺の観光スポット、飲食のおすすめなどに音声で応答し、やり取りを重ねるごとに学習しました。</p>
              <p className={P}>Connieの事例は、AIが人間のコンシェルジュや執事を置き換えるのではなく、定型的な問い合わせを引き受けることで人間のスタッフをより高度な対人サービスに集中させるという、本記事の中心的な主張を早くも2016年の時点で具体化していたものと言えます。</p>
            </div>
            <SourceShot
              src="/images/news-sources/aihosp-ibm-connie.jpg"
              alt="HiltonとIBMがロボットコンシェルジュConnieを試験導入したことを伝えるIBM公式ニュースルームのスクリーンショット"
              media="IBM Newsroom"
              url="https://uk.newsroom.ibm.com/2016-Mar-09-Hilton-and-IBM-Pilot-Connie-The-Worlds-First-Watson-Enabled-Hotel-Concierge"
            />
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">発表媒体</dt><dd className="text-[#333] mt-0.5">Hospitality Net</dd></div>
                  <div><dt className="text-[#8f7a56]">著者</dt><dd className="text-[#333] mt-0.5">Kate Fairweather</dd></div>
                  <div><dt className="text-[#8f7a56]">カテゴリ</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                  <div><dt className="text-[#8f7a56]">市場規模（2031予測）</dt><dd className="text-[#333] mt-0.5">約703億ドル</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
